import { useEffect, useRef, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import {
  type BillingInterval,
  type BillingStatus,
  type PlanOption,
  type PlanTier,
  canWriteArea,
  formatEur,
  localDayKey,
  parseDbTimestamp,
} from '@wf/shared'
import { ApiError, type BillingStatusProvisioned } from '../api/client'
import { useAuth } from '../app/auth'
import { formatDateDE } from '../time/format'
import { redirectToProvider } from './redirect'
import {
  useAboInvoices,
  useBillingCancel,
  useBillingChangePlan,
  useBillingCheckout,
  useBillingReconcilePolling,
  useBillingStatus,
  useSeatUsers,
} from './useBilling'

const PLAN_LABEL: Record<PlanTier, string> = { solo: 'Solo', team: 'Team', werkstatt: 'Werkstatt' }
const INTERVAL_LABEL: Record<BillingInterval, string> = { monthly: 'Monat', yearly: 'Jahr' }
/** Adverb-Form fürs Einzugs-Intervall („danach monatlich …"). */
const INTERVAL_ADVERB: Record<BillingInterval, string> = { monthly: 'monatlich', yearly: 'jährlich' }
/** 9999 = „unbegrenzt" (Werkstatt-Tarif), sonst die Zahl. */
function limitLabel(limit: number): string {
  return limit >= 9999 ? 'unbegrenzt' : String(limit)
}

// Abo & Lizenzen (Chef). Diese Seite ist der EINZIGE Weg, den Zahlweg einzurichten oder zu kündigen —
// sie bleibt darum auch bei readonly/billing_only erreichbar. Geld immer aus den `…Gross`-Feldern
// (der Kunde zahlt brutto); die abrechnungsrelevante Seat-Zahl ist `seatsUsed` aus dem Status.

const STATUS_LABEL: Record<BillingStatus, string> = {
  trialing: 'Testphase',
  active: 'Aktiv',
  past_due: 'Zahlung fällig',
  canceled: 'Gekündigt',
}
const STATUS_TONE: Record<BillingStatus, string> = {
  trialing: 'info',
  active: 'ok',
  past_due: 'warn',
  canceled: 'muted',
}

const POLL_LIMIT_MS = 60_000

/** Übersetzt einen Checkout-Fehler in einen nutzbaren Hinweis: die actionable Backend-Meldung (z. B.
 *  „Bitte zuerst eine E-Mail-Adresse im Profil hinterlegen.") sichtbar machen, statt sie zu verschlucken. */
function checkoutHint(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.status === 503) return 'Zahlung aktuell nicht verfügbar (Anbieter nicht konfiguriert). Bitte später erneut versuchen.'
    // 4xx trägt eine konkrete, nutzergerechte Ursache (z. B. fehlende E-Mail) → direkt anzeigen.
    if (err.status < 500 && err.message) return err.message
  }
  return 'Der Zahlweg konnte nicht gestartet werden. Bitte erneut versuchen.'
}

/** Rahmen + Chef-Guard. Nur mit `werkstatt_einstellungen`-Schreibrecht lädt/rendert die Seite den Status. */
export function BillingPage() {
  const { user } = useAuth()
  const allowed = !!user && canWriteArea(user, 'werkstatt_einstellungen')
  return (
    <section className="wf-view wf-abo">
      <div>
        <h2 className="viewtitle">Abo &amp; Lizenzen</h2>
        <p className="sub">Zahlweg, Seats und Kündigung deiner Cevio-Lizenz</p>
      </div>
      {allowed ? (
        <BillingPanel />
      ) : (
        <div className="error" role="alert">
          Für diesen Bereich fehlt dir die Berechtigung.
        </div>
      )}
    </section>
  )
}

type CheckoutPhase = 'idle' | 'polling' | 'done' | 'canceled' | 'timeout'

function BillingPanel() {
  const [params, setParams] = useSearchParams()
  const [phase, setPhase] = useState<CheckoutPhase>('idle')
  const status = useBillingStatus()
  // Nach dem Rücksprung aktiv den echten Mandatsstatus vom Anbieter ziehen (robuster als nur der Webhook);
  // das Ergebnis landet im Status-Cache, den `status` liest.
  useBillingReconcilePolling(phase === 'polling')
  const seatUsers = useSeatUsers()
  const checkout = useBillingCheckout()
  const cancel = useBillingCancel()
  const [planDialog, setPlanDialog] = useState(false)

  // Rückkehr vom Anbieter (…/einstellungen/abo?mandat=ok|abbruch) genau EINMAL auswerten und den
  // Parameter aus der URL entfernen (ein Reload soll das Polling nicht erneut auslösen).
  const handledReturn = useRef(false)
  useEffect(() => {
    if (handledReturn.current) return
    const mandat = params.get('mandat')
    if (!mandat) return
    handledReturn.current = true
    setPhase(mandat === 'ok' ? 'polling' : 'canceled')
    const next = new URLSearchParams(params)
    next.delete('mandat')
    setParams(next, { replace: true })
  }, [params, setParams])

  // Polling beenden, sobald das (asynchron aktiv werdende) Mandat greift.
  const s = status.data
  useEffect(() => {
    if (phase === 'polling' && s?.provisioned && (s.mandatePresent || s.billingStatus === 'active')) setPhase('done')
  }, [phase, s])
  // Ein einziges 60-s-Zeitlimit ab Poll-Beginn (NICHT bei jedem Poll neu starten).
  useEffect(() => {
    if (phase !== 'polling') return
    const t = setTimeout(() => setPhase((p) => (p === 'polling' ? 'timeout' : p)), POLL_LIMIT_MS)
    return () => clearTimeout(t)
  }, [phase])

  if (status.isLoading) return <div className="muted">Abo wird geladen…</div>
  if (status.error || !status.data) {
    const forbidden = status.error instanceof ApiError && status.error.status === 403
    return (
      <div className="error" role="alert">
        {forbidden ? 'Für diesen Bereich fehlt dir die Berechtigung.' : 'Abo-Status konnte nicht geladen werden.'}
      </div>
    )
  }
  const st = status.data

  function startCheckout() {
    checkout.mutate({}, { onSuccess: ({ redirectUrl }) => redirectToProvider(redirectUrl) })
  }
  function requestCancel() {
    if (!st.provisioned) return
    if (
      !window.confirm(
        'Abo zum Ende des bezahlten Zeitraums kündigen?\n\nDein Zugang bleibt bis dahin voll bestehen — danach wird ' +
          'nichts mehr eingezogen. Eine Rückerstattung für den laufenden Zeitraum gibt es nicht.',
      )
    )
      return
    cancel.mutate(st.version)
  }

  const showCheckout = !st.provisioned || !st.mandatePresent || st.accessLevel !== 'full'
  // Kündigen anbieten, solange nicht schon gekündigt UND keine Kündigung bereits vorgemerkt ist (cancelAt).
  const showCancel = st.provisioned && st.billingStatus !== 'canceled' && !st.cancelAt
  const cancelStale = cancel.error instanceof ApiError && cancel.error.status === 409

  return (
    <div className="wf-abo__stack">
      {/* Hinweise zur Mandats-Rückkehr / laufendem Polling */}
      {phase === 'polling' && (
        <div className="wf-abo__note wf-abo__note--info" role="status">
          Zahlweg wird eingerichtet… das Mandat wird gleich aktiv.
        </div>
      )}
      {phase === 'done' && (
        <div className="wf-abo__note wf-abo__note--ok" role="status">
          Zahlweg ist eingerichtet. Danke!
        </div>
      )}
      {phase === 'timeout' && (
        <div className="wf-abo__note wf-abo__note--info" role="status">
          Die Einrichtung dauert noch an. Aktualisiere die Seite in einem Moment.
        </div>
      )}
      {phase === 'canceled' && (
        <div className="wf-abo__note" role="status">
          Die Einrichtung des Zahlwegs wurde abgebrochen.
        </div>
      )}

      {/* Einschränkungs-Hinweis, wenn der Zugang eingeschränkt ist */}
      {st.accessLevel === 'readonly' && (
        <div className="wf-abo__note wf-abo__note--warn" role="alert">
          Die letzte Zahlung ist fehlgeschlagen — Schreibaktionen sind gesperrt. Bitte den Zahlweg prüfen.
        </div>
      )}
      {st.accessLevel === 'billing_only' && (
        <div className="wf-abo__note wf-abo__note--warn" role="alert">
          Das Abo ist beendet — nur die Abo-Verwaltung ist erreichbar. Richte einen Zahlweg ein, um Cevio weiter zu nutzen.
        </div>
      )}

      {/* a) Status-Karte */}
      <div className="wf-abo__card">
        <div className="wf-abo__cardhead">
          <h3>Status</h3>
          {st.provisioned ? (
            <span className={`wf-abo__badge wf-abo__badge--${STATUS_TONE[st.billingStatus]}`}>{STATUS_LABEL[st.billingStatus]}</span>
          ) : (
            <span className="wf-abo__badge wf-abo__badge--info">Testphase</span>
          )}
        </div>
        {st.provisioned && st.billingStatus === 'trialing' && st.trialEnd && (
          <p className="wf-abo__line">
            Testphase endet am {formatDateDE(localDayKey(parseDbTimestamp(st.trialEnd)))}
            {!st.cancelAt && <> — dann erfolgt der erste Einzug, danach {INTERVAL_ADVERB[st.billingInterval]} zum selben Kalendertag</>}.
          </p>
        )}
        {/* Nächster Einzug beim laufenden Abo: an welchem Tag sich der Turnus verlängert. Nicht, wenn eine
            Kündigung bereits vorgemerkt ist (dann wird nichts mehr eingezogen — der cancelAt-Hinweis unten greift). */}
        {st.provisioned && st.billingStatus === 'active' && st.currentPeriodEnd && !st.cancelAt && (
          <p className="wf-abo__line">
            Nächster Einzug am {formatDateDE(localDayKey(parseDbTimestamp(st.currentPeriodEnd)))} — danach{' '}
            {INTERVAL_ADVERB[st.billingInterval]} zum selben Kalendertag.
          </p>
        )}
        {/* Kündigung zum Periodenende ist vorgemerkt: Status bleibt aktiv/Testphase → voller Zugang bis dahin. */}
        {st.provisioned && st.cancelAt && st.billingStatus !== 'canceled' && (
          <p className="wf-abo__line">
            Gekündigt zum {formatDateDE(localDayKey(parseDbTimestamp(st.cancelAt)))} — dein Zugang bleibt bis dahin
            bestehen, danach wird nichts mehr eingezogen (keine Rückerstattung).
          </p>
        )}
        <p className="wf-abo__line">
          Zahlweg: {st.provisioned && st.mandatePresent ? 'SEPA-Lastschrift eingerichtet' : 'Noch kein Zahlweg hinterlegt'}
        </p>
      </div>

      {/* b) Tarif / Beitrag */}
      <div className="wf-abo__card">
        <div className="wf-abo__cardhead">
          <h3>Tarif</h3>
          {st.provisioned && (
            <button type="button" className="wf-btn wf-btn--ghost wf-btn--sm" onClick={() => setPlanDialog(true)}>
              Tarif ändern
            </button>
          )}
        </div>
        {st.provisioned && st.planTier ? (
          <>
            <div className="wf-abo__amount">
              {formatEur(st.priceCentsNet)}
              <span className="wf-abo__per"> / {INTERVAL_LABEL[st.billingInterval]} (exkl. MwSt)</span>
            </div>
            <p className="wf-abo__line">
              Tarif <strong>{PLAN_LABEL[st.planTier]}</strong> · {st.seatsUsed} von {limitLabel(st.userLimit)}{' '}
              {st.userLimit === 1 ? 'Nutzer' : 'Nutzern'} belegt
            </p>
            <p className="muted">zzgl. 19 % USt = {formatEur(st.priceCentsGross)} inkl. MwSt</p>
            {(st.scheduledPlanTier || st.scheduledBillingInterval) && (
              <p className="wf-abo__line">
                Zum nächsten Abrechnungszeitraum:{' '}
                {st.scheduledPlanTier ? `Wechsel auf Tarif ${PLAN_LABEL[st.scheduledPlanTier]}` : ''}
                {st.scheduledPlanTier && st.scheduledBillingInterval ? ', ' : ''}
                {st.scheduledBillingInterval ? `Abrechnung ${st.scheduledBillingInterval === 'yearly' ? 'jährlich' : 'monatlich'}` : ''}
                .
              </p>
            )}
          </>
        ) : (
          <p className="wf-abo__line">
            {st.seatsUsed} aktive{st.seatsUsed === 1 ? 'r Nutzer' : ' Nutzer'} — noch kein Tarif gewählt.{' '}
            {st.provisioned ? 'Wähle über „Tarif ändern" einen Tarif.' : 'Der Beitrag entsteht erst mit dem ersten Zahlweg.'}
          </p>
        )}
      </div>

      {/* c) Seat-Liste (nur informativ) */}
      {seatUsers.data && (
        <div className="wf-abo__card">
          <h3>Belegte Seats</h3>
          <ul className="wf-abo__seats">
            {seatUsers.data.users
              .filter((u) => u.active === 1)
              .map((u) => (
                <li key={u.id} className="wf-abo__seat">
                  <span className="wf-abo__dot" style={{ background: u.color }} aria-hidden="true" />
                  <span>{u.name}</span>
                  {u.position && <span className="muted">{u.position}</span>}
                </li>
              ))}
          </ul>
          <p className="muted">
            {st.provisioned && st.planTier
              ? `Tarif ${PLAN_LABEL[st.planTier]}: ${st.seatsUsed} von ${limitLabel(st.userLimit)} Nutzern belegt.`
              : `${st.seatsUsed} aktive Nutzer.`}
          </p>
        </div>
      )}

      {/* d) Aktionen */}
      <div className="wf-abo__actions">
        {showCheckout && (
          <button type="button" className="wf-btn" onClick={startCheckout} disabled={checkout.isPending}>
            {checkout.isPending ? 'Weiterleitung…' : 'Abonnieren'}
          </button>
        )}
        {showCancel && (
          <button type="button" className="wf-btn wf-btn--danger" onClick={requestCancel} disabled={cancel.isPending}>
            {cancel.isPending ? 'Wird gekündigt…' : 'Abo kündigen'}
          </button>
        )}
        {/* „Status aktualisieren" entfällt: der Mandatsstatus kommt jetzt still über den Webhook + das
            Reconcile-Polling nach der Checkout-Rückkehr (useBillingReconcilePolling) — kein Handknopf mehr. */}
      </div>
      {checkout.error && (
        <div className="wf-abo__note wf-abo__note--warn" role="alert">
          {checkoutHint(checkout.error)}
        </div>
      )}
      {cancelStale && (
        <div className="wf-abo__note wf-abo__note--warn" role="alert">
          Der angezeigte Stand war veraltet und wurde aktualisiert — bitte erneut auf „Abo kündigen" tippen.
        </div>
      )}
      {cancel.error && !cancelStale && (
        <div className="wf-abo__note wf-abo__note--warn" role="alert">
          Die Kündigung ist fehlgeschlagen. Bitte erneut versuchen.
        </div>
      )}

      {/* e) Rechnungen — die monatlichen Abo-Belege (Cevio → Werkstatt), als PDF druckbar. */}
      <AboInvoicesCard />

      {planDialog && st.provisioned && <ChangePlanDialog st={st} onClose={() => setPlanDialog(false)} />}
    </div>
  )
}

/** Dialog „Tarif ändern": Intervall-Umschalter + die drei Tarife (aus st.availablePlans) zur Auswahl. */
function ChangePlanDialog({ st, onClose }: { st: BillingStatusProvisioned; onClose: () => void }) {
  const changePlan = useBillingChangePlan()
  const [tier, setTier] = useState<PlanTier>(st.planTier ?? 'team')
  const [interval, setInterval] = useState<BillingInterval>(st.billingInterval)
  const isNoop = tier === st.planTier && interval === st.billingInterval
  const errMsg =
    changePlan.error instanceof ApiError ? changePlan.error.message : changePlan.error ? 'Der Tarifwechsel ist fehlgeschlagen.' : null

  function submit() {
    changePlan.mutate({ planTier: tier, billingInterval: interval, version: st.version }, { onSuccess: onClose })
  }

  return (
    <div className="wf-plandlg" role="presentation" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="wf-plandlg__panel" role="dialog" aria-modal="true" aria-label="Tarif ändern">
        <h3>Tarif ändern</h3>
        <div className="wf-abo__interval" role="group" aria-label="Abrechnungsintervall">
          {(['monthly', 'yearly'] as BillingInterval[]).map((iv) => (
            <button
              key={iv}
              type="button"
              aria-pressed={interval === iv}
              className={`wf-btn wf-btn--sm ${interval === iv ? '' : 'wf-btn--ghost'}`}
              onClick={() => setInterval(iv)}
            >
              {iv === 'monthly' ? 'Monatlich' : 'Jährlich (2 Monate gratis)'}
            </button>
          ))}
        </div>
        <div className="wf-abo__plans">
          {st.availablePlans.map((p) => (
            <PlanChoice
              key={p.tier}
              plan={p}
              interval={interval}
              selected={tier === p.tier}
              current={st.planTier === p.tier}
              onSelect={() => setTier(p.tier)}
            />
          ))}
        </div>
        <p className="muted">
          Upgrade wirkt sofort (die Differenz wird anteilig berechnet); Downgrade und Intervallwechsel erst zum nächsten
          Abrechnungszeitraum. Ein Downgrade geht nur, wenn deine aktive Nutzerzahl ins Tarif-Limit passt.
        </p>
        {errMsg && (
          <div className="wf-abo__note wf-abo__note--warn" role="alert">
            {errMsg}
          </div>
        )}
        <div className="wf-form__actions">
          <button type="button" className="wf-btn wf-btn--ghost" onClick={onClose}>
            Abbrechen
          </button>
          <button type="button" className="wf-btn" onClick={submit} disabled={isNoop || changePlan.isPending}>
            {changePlan.isPending ? 'Wird gespeichert…' : 'Tarif übernehmen'}
          </button>
        </div>
      </div>
    </div>
  )
}

/** Eine auswählbare Tarif-Kachel im „Tarif ändern"-Dialog (zeigt den Preis fürs gewählte Intervall). */
function PlanChoice({
  plan,
  interval,
  selected,
  current,
  onSelect,
}: {
  plan: PlanOption
  interval: BillingInterval
  selected: boolean
  current: boolean
  onSelect: () => void
}) {
  const price = interval === 'yearly' ? plan.yearly : plan.monthly
  return (
    <button
      type="button"
      className={`wf-abo__plan${selected ? ' wf-abo__plan--sel' : ''}`}
      onClick={onSelect}
      aria-pressed={selected}
    >
      <span className="wf-abo__plan-name">
        {PLAN_LABEL[plan.tier]}
        {current ? ' · aktuell' : ''}
      </span>
      <span className="wf-abo__plan-price wf-tnum">
        {formatEur(price.net)} <span className="muted">/ {INTERVAL_LABEL[interval]} netto</span>
      </span>
      <span className="muted">bis {limitLabel(plan.userLimit)} Nutzer</span>
    </button>
  )
}

/** Liste der monatlichen Abo-Rechnungen mit Klick auf die Druck-/PDF-Ansicht. Rein informativ (Lesepfad). */
function AboInvoicesCard() {
  const invoices = useAboInvoices()
  const rows = invoices.data?.invoices ?? []
  return (
    <div className="wf-abo__card">
      <h3>Rechnungen</h3>
      {invoices.isLoading ? (
        <p className="muted">Rechnungen werden geladen…</p>
      ) : rows.length === 0 ? (
        <p className="muted">Noch keine Rechnungen vorhanden — die erste entsteht mit dem ersten Einzug.</p>
      ) : (
        <ul className="wf-abo__invoices">
          {rows.map((inv) => (
            <li key={inv.id} className="wf-abo__invoice">
              <Link to={`/print/abo-invoice/${inv.id}`} className="wf-abo__invoice-link">
                <span className="wf-abo__invoice-no wf-tnum">{inv.invoiceNumber}</span>
                <span className="wf-abo__invoice-date">{formatDateDE(inv.issueDate)}</span>
                <span className="wf-abo__invoice-sum wf-tnum">{formatEur(inv.grossTotal)}</span>
              </Link>
            </li>
          ))}
        </ul>
      )}
      <p className="muted">Klick auf eine Rechnung öffnet die Druckansicht — dort „Drucken / als PDF" wählen.</p>
    </div>
  )
}
